import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Loader2, FlaskConical, Play, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

const SCENARIOS = [
  { value: "material_transition", label: "Material Transition (paper → polymer)", inputs: [{ k: "target_substrate", label: "Target substrate", type: "select", options: ["polymer", "paper"] }] },
  { value: "renewable", label: "Renewable Energy Adoption", inputs: [{ k: "renewable_pct", label: "Renewable %", type: "number", default: 50 }] },
  { value: "spoilage", label: "Spoilage Reduction", inputs: [{ k: "target_spoilage_pct", label: "Target spoilage %", type: "number", default: 1.5 }] },
  { value: "logistics", label: "Logistics Optimization", inputs: [{ k: "distance_reduction_pct", label: "Distance reduction %", type: "number", default: 30 }] },
];

export default function Scenarios() {
  const [denomination, setDenomination] = useState(1000);
  const [type, setType] = useState("material_transition");
  const [name, setName] = useState("Paper → Polymer transition for ₱500");
  const [inputs, setInputs] = useState({ target_substrate: "polymer" });

  // when user changes denomination/type, suggest sensible defaults
  useEffect(() => {
    if (type === "material_transition") {
      // ₱1000 already polymer — flipping to paper shows non-zero delta; for paper-only denoms, flip to polymer
      const target = denomination === 1000 ? "paper" : "polymer";
      setInputs({ target_substrate: target });
      setName(`${target === "polymer" ? "Polymer transition" : "Paper revert"} for ₱${denomination}`);
    } else if (type === "renewable") {
      setInputs({ renewable_pct: 60 });
      setName(`60% renewable energy for ₱${denomination}`);
    } else if (type === "spoilage") {
      setInputs({ target_spoilage_pct: 1.0 });
      setName(`Reduce spoilage to 1% for ₱${denomination}`);
    } else if (type === "logistics") {
      setInputs({ distance_reduction_pct: 30 });
      setName(`Logistics −30% for ₱${denomination}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, denomination]);
  const [history, setHistory] = useState([]);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/scenarios").then((r) => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const conf = SCENARIOS.find((s) => s.value === type);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/scenarios", { name, scenario_type: type, denomination, inputs });
      setLatest(r.data);
      toast.success("Scenario complete");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const compareData = latest ? [
    { label: "Baseline", tco2e: latest.baseline_pcf, fill: "#64748b" },
    { label: "Scenario", tco2e: latest.scenario_pcf, fill: latest.delta_pct < 0 ? "#166534" : "#B45309" },
  ] : [];

  return (
    <div data-testid="scenarios-page" className="space-y-6">
      <div>
        <div className="overline text-emerald-800 flex items-center gap-2"><FlaskConical className="w-3 h-3" /> What-if</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Scenario Simulator</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">Test alternative substrates, renewable adoption, spoilage targets and logistics changes — modeled against a real BSP batch baseline.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 border border-slate-200 p-6 bg-white space-y-4">
          <div>
            <label className="overline text-slate-500 block mb-1">Scenario Name</label>
            <input data-testid="scen-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 text-sm" />
          </div>
          <div>
            <label className="overline text-slate-500 block mb-1">Denomination (baseline)</label>
            <select data-testid="scen-denomination" value={denomination} onChange={(e) => setDenomination(Number(e.target.value))} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white">
              {[20,50,100,200,500,1000].map((d) => <option key={d} value={d}>₱{d}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-slate-500 block mb-1">Scenario Type</label>
            <select data-testid="scen-type" value={type} onChange={(e) => { setType(e.target.value); setInputs({}); }} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white">
              {SCENARIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {conf?.inputs.map((inp) => (
            <div key={inp.k}>
              <label className="overline text-slate-500 block mb-1">{inp.label}</label>
              {inp.type === "select" ? (
                <select data-testid={`scen-input-${inp.k}`} value={inputs[inp.k] ?? inp.options[0]} onChange={(e) => setInputs({ ...inputs, [inp.k]: e.target.value })} className="w-full px-3 py-2 border border-slate-300 text-sm bg-white">
                  {inp.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input data-testid={`scen-input-${inp.k}`} type="number" value={inputs[inp.k] ?? inp.default ?? 0} onChange={(e) => setInputs({ ...inputs, [inp.k]: Number(e.target.value) })} className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
              )}
            </div>
          ))}
          <button data-testid="run-scenario" onClick={run} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 py-3 bg-emerald-800 text-white text-xs uppercase tracking-[0.2em] hover:bg-emerald-900">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4" /> Run scenario</>}
          </button>
        </div>

        <div className="lg:col-span-2 border border-slate-200 p-6 bg-white">
          {latest ? (
            <>
              <div className="overline text-emerald-800">Result</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{latest.name}</div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="border border-slate-200 p-4">
                  <div className="overline text-slate-500">Baseline</div>
                  <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{latest.baseline_pcf} <span className="text-xs text-slate-500">tCO₂e</span></div>
                </div>
                <div className="border border-slate-200 p-4">
                  <div className="overline text-slate-500">Scenario</div>
                  <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{latest.scenario_pcf} <span className="text-xs text-slate-500">tCO₂e</span></div>
                </div>
                <div className={`border p-4 ${latest.delta_pct < 0 ? "border-emerald-700 bg-emerald-50" : "border-amber-600 bg-amber-50"}`}>
                  <div className="overline text-slate-500">Delta</div>
                  <div className="font-heading text-2xl font-bold mt-1 flex items-center gap-1">
                    {latest.delta_pct < 0 ? <TrendingDown className="w-5 h-5 text-emerald-800" /> : <TrendingUp className="w-5 h-5 text-amber-700" />}
                    {latest.delta_pct > 0 ? "+" : ""}{latest.delta_pct}%
                  </div>
                </div>
              </div>

              <div className="h-64 mt-6">
                <ResponsiveContainer>
                  <BarChart data={compareData}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="tco2e">
                      {compareData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-slate-700 mt-4 italic">{latest.insights}</p>
            </>
          ) : (
            <div className="text-slate-500 h-full grid place-items-center">Run a scenario to see results.</div>
          )}
        </div>
      </div>

      <div className="border border-slate-200 p-6 bg-white">
        <div className="overline text-emerald-800 mb-3">Recent Scenarios</div>
        <table data-testid="scen-history" className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-500">Name</th>
              <th className="px-3 py-2 overline text-slate-500">Type</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">Baseline</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">Scenario</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">Δ%</th>
            </tr>
          </thead>
          <tbody>
            {history.map((s) => (
              <tr key={s.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-semibold text-slate-900">{s.name}</td>
                <td className="px-3 py-2 text-xs uppercase tracking-[0.18em] text-slate-600">{s.scenario_type}</td>
                <td className="px-3 py-2 text-right font-mono">{s.baseline_pcf}</td>
                <td className="px-3 py-2 text-right font-mono">{s.scenario_pcf}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${s.delta_pct < 0 ? "text-emerald-800" : "text-amber-700"}`}>{s.delta_pct > 0 ? "+" : ""}{s.delta_pct}%</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-slate-500">No scenarios yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
